// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building's storeys — the middle level of bygning › etage › rum. The
 * stored storeys come from `GET /storeys` (ruxd-6tz, `bygning/spatial.ts`);
 * this module is the fallback heuristic for a project with none stored or a
 * server without the route (CONTRACT-GAPS.md gap 7): the room centroids are
 * sorted by height and split wherever two consecutive centroids are further
 * apart than a storey gap; the clusters are named by order (Stue, 1. sal,
 * 2. sal, …; a lone top room called Loft names its storey). Nothing is
 * persisted. Pure: no React, no DOM.
 */

export interface Storey {
  /** 0 for the lowest storey, counting up. Not stable across derivations. */
  id: number;
  name: string;
  /** World z of the storey's lowest point (its rooms' boxes, or centroid − 1.5 m). */
  zMin: number;
  /** World z of the storey's highest point (its rooms' boxes, or centroid + 1.5 m). */
  zMax: number;
  /** Member room ids, ascending. */
  roomIds: number[];
  /** Marked as the ground (entrance) storey (stored storeys, `is_ground`); the picker's S / G. */
  isGround?: boolean;
}

/** One room as the clustering sees it: its centroid height and, when known, its box's z extent. */
export interface StoreyRoomInput {
  id: number;
  name: string;
  /** Centroid z (world). */
  z: number;
  zMin?: number;
  zMax?: number;
}

/** Centroids further apart than this (metres) start a new storey. */
export const DEFAULT_STOREY_GAP = 1.8;
/** Half a storey's height, for a room whose box is not known. */
const HALF_HEIGHT = 1.5;

/** The storey name for position `index` (0 = Stue). */
function storeyName(index: number): string {
  return index === 0 ? 'Stue' : `${index}. sal`;
}

/**
 * Storeys from room centroids, bottom up. Fewer than two storeys is no
 * hierarchy at all, so that returns `[]` and the UI hides the level.
 * Rooms with a non-finite centroid are ignored.
 */
export function deriveStoreys(rooms: readonly StoreyRoomInput[], opts?: { gap?: number }): Storey[] {
  const gap = opts?.gap ?? DEFAULT_STOREY_GAP;
  const sorted = rooms.filter((r) => Number.isFinite(r.z)).sort((a, b) => a.z - b.z || a.id - b.id);
  if (sorted.length === 0) return [];

  const clusters: StoreyRoomInput[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].z - sorted[i - 1].z > gap) clusters.push([sorted[i]]);
    else clusters[clusters.length - 1].push(sorted[i]);
  }
  if (clusters.length < 2) return [];

  return clusters.map((members, index) => {
    let zMin = Infinity;
    let zMax = -Infinity;
    for (const r of members) {
      const lo = r.zMin !== undefined && Number.isFinite(r.zMin) ? r.zMin : r.z - HALF_HEIGHT;
      const hi = r.zMax !== undefined && Number.isFinite(r.zMax) ? r.zMax : r.z + HALF_HEIGHT;
      if (lo < zMin) zMin = lo;
      if (hi > zMax) zMax = hi;
    }
    const top = index === clusters.length - 1;
    const loft = top && members.length === 1 && members[0].name.trim().toLowerCase() === 'loft';
    return {
      id: index,
      name: loft ? 'Loft' : storeyName(index),
      zMin,
      zMax,
      roomIds: members.map((r) => r.id).sort((a, b) => a - b),
    };
  });
}

/** Storey `id`, or null when it is not in the list. */
export function storeyById(storeys: readonly Storey[], id: number | null): Storey | null {
  if (id === null) return null;
  return storeys.find((s) => s.id === id) ?? null;
}

/** The storey room `roomId` belongs to, or null. */
export function storeyOfRoom(storeys: readonly Storey[], roomId: number | null): Storey | null {
  if (roomId === null) return null;
  return storeys.find((s) => s.roomIds.includes(roomId)) ?? null;
}

/** The room ids of storey `id`; empty when there is no such storey. */
export function storeyRooms(storeys: readonly Storey[], id: number | null): number[] {
  return storeyById(storeys, id)?.roomIds ?? [];
}

/**
 * Whether world height `z` lies on `storey`: from its floor up to, not
 * including, its top. A non-finite height (a pose that decoded to NaN) is on
 * no storey: under a storey focus such a marker is dropped rather than
 * guessed onto one; without a focus it is shown as before.
 */
export function onStorey(z: number, storey: Pick<Storey, 'zMin' | 'zMax'>): boolean {
  return Number.isFinite(z) && z >= storey.zMin && z < storey.zMax;
}

/**
 * The panorama markers to show under a storey focus: those taken on the
 * storey (their capture height within its span); all of them without a
 * focus. Markers are placeable panoramas only — one without a pose never
 * reaches this list.
 */
export function markersOnStorey<T extends { placement: { position: readonly [number, number, number] } }>(
  markers: readonly T[],
  storey: Pick<Storey, 'zMin' | 'zMax'> | null,
): T[] {
  if (!storey) return [...markers];
  return markers.filter((m) => onStorey(m.placement.position[2], storey));
}

/**
 * Height above the floor a plan is cut at (metres) — the usual architectural
 * cut plane: below the window heads, above the sills and the furniture.
 */
export const PLAN_CUT_HEIGHT = 1.2;

/**
 * World height of the locked plan's section through `storey`: its floor plus
 * {@link PLAN_CUT_HEIGHT}, never above its top. Not at the top itself —
 * an imported storey runs up to the next level, whose floor slab sits just
 * under it and would cover the whole plan.
 */
export function planCutHeight(storey: Pick<Storey, 'zMin' | 'zMax'>): number {
  return Math.min(storey.zMax, storey.zMin + PLAN_CUT_HEIGHT);
}
